import React, {Fragment, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useClinicalNoteStyles} from './clinicalNotes';
import _ from 'lodash';
import OngoingSpellProgress from '../admissions/ongoingSpellProgress';
import {IconButton} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SingleNoteComponent from './singleNote';
import {clinicalNoteTypeDict} from '../../store/reducers/dicts';
import clsx from 'clsx';
import {noteFilters} from '../../store/reducers/notes';
import AttendanceChip from './component/attendanceChip';
import Chip from '@mui/material/Chip';
import {getConfig} from '../../util/configUtil';
import moment from 'moment';

const AdmissionClinicalNotes = ({
                                    filterText,
                                    noteAdmission,
                                    hideNote,
                                    editNote,
                                    disableSave,
                                    showNoteAudit,
                                    checkLastOfType,
                                    markAsError,
                                }) => {
    const classes = useClinicalNoteStyles();
    const {t} = useTranslation();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');

    // Selector
    const clinicalNoteType = useSelector(clinicalNoteTypeDict);
    const filter = useSelector(noteFilters);

    // Expand state
    const isFromReviewCtx = noteAdmission?.admission === null;
    const isActive = _.isNil(noteAdmission?.admission?.endDate);
    const [expanded, setExpanded] = useState(isActive && !isFromReviewCtx);

    const [notes, setNotes] = useState([]);
    const isED = noteAdmission?.admission?.attendanceType === 'E';

    useEffect(() => {
        setExpanded(filter.expanded || isActive);
    }, [filter.expanded]);

    useEffect(() => {
        const grouped = _.groupBy(noteAdmission?.note, n => n['episodeId']);
        let finalObj = [];

        if (!_.isEmpty(noteAdmission?.admission?.episodes)) {
            const ordered = _.orderBy(noteAdmission?.admission?.episodes, ['startDate'], ['desc']);
            _.forEach(ordered, ep => {
                finalObj.push({
                    id: ep?.episodeNumber,
                    notes: grouped[ep?.episodeNumber] || [],
                    startDate: ep?.startDate,
                });
            });
        }

        finalObj.push({
            id: 'no_episode_number',
            notes: grouped['null'],
            startDate: null,
        });
        setNotes(finalObj);
    }, [noteAdmission?.note]);

    const isVisible = (note) => {
        if (!expanded) return false;
        if (!filterText) return true;
        if (note?.note) {
            if (note.note.startsWith('data:image/png;base64')) return false;
            return note.note.toLowerCase().includes(filterText.toLowerCase());
        }
        return false;
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    const renderMainChip = () => {
        return (
            <div className={clsx(classes.divRow, classes.noteAdmissionRoot)} style={{gap: 10}}>
                <AttendanceChip admission={noteAdmission.admission} reviewCtx={isFromReviewCtx}/>
                <IconButton
                    size="small"
                    onClick={handleExpand}
                >
                    {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                </IconButton>

                {!_.isEmpty(noteAdmission.admission?.episodes) &&
                    <Chip
                        label={`${noteAdmission.admission?.episodes?.length || 0} ${t('Ep.')}`}
                        variant="outlined"
                    />
                }

                {(isActive && !isFromReviewCtx) && <OngoingSpellProgress/>}
            </div>
        );
    };

    return (
        <div>
            {renderMainChip()}

            {notes.map(note => {
                if (_.isEmpty(note?.notes) || !expanded) return null;

                let label = `${t(`Inpatient Episode ${moment(note?.startDate).format(dateFormat)}`)}`;
                if (!note?.startDate) label = t('Inpatient other notes');

                return (
                    <Fragment key={note?.id}>
                        {(!isED && !isFromReviewCtx) &&
                            <Chip
                                label={label}
                                variant="outlined"
                                style={{marginBottom: 20}}
                            />
                        }

                        {note?.notes?.map((note, index) => {
                            if (!isVisible(note)) return null;

                            return (
                                <SingleNoteComponent
                                    filterText={filterText}
                                    index={index}
                                    onHide={hideNote(note)}
                                    onEdit={editNote(note)}
                                    onAudit={showNoteAudit(note)}
                                    isLastOfType={checkLastOfType(note)}
                                    handleError={() => markAsError(note)}
                                    key={note.__id}
                                    note={note}
                                    disableSave={disableSave}
                                    clinicalNoteTypes={clinicalNoteType}
                                />
                            );
                        })}
                    </Fragment>
                );
            })}
        </div>
    );
};

export default AdmissionClinicalNotes;
